import { useEffect } from "react";
import EngineeringIcon from "@mui/icons-material/Engineering";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Controller,
  useForm,
} from "react-hook-form";

import {
  AssignTechnicianPayload,
  ServiceTicket,
} from "../types";

interface TechnicianOption {
  id: string;

  name: string;
}

interface AssignTechnicianDialogProps {
  open: boolean;

  loading?: boolean;

  ticket?: ServiceTicket | null;

  technicians: TechnicianOption[];

  onClose: () => void;

  onSubmit: (
    payload: AssignTechnicianPayload
  ) => void;
}

const defaultValues: AssignTechnicianPayload = {
  technicianId: "",

  remarks: "",
};

const AssignTechnicianDialog = ({
  open,
  loading = false,
  ticket,
  technicians,
  onClose,
  onSubmit,
}: AssignTechnicianDialogProps) => {
  const {
    control,
    handleSubmit,
    reset,
  } =
    useForm<AssignTechnicianPayload>({
      defaultValues,
    });

  useEffect(() => {
    reset({
      technicianId:
        ticket?.assignedTechnicianId ??
        "",

      remarks: "",
    });
  }, [ticket, reset]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Assign Technician
      </DialogTitle>

      <DialogContent dividers>
        <Stack
          spacing={3}
          sx={{ mt: 1 }}
        >
          {ticket && (
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  Ticket Number
                </Typography>

                <Typography
                  fontWeight={600}
                >
                  {ticket.ticketNumber}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  Vehicle
                </Typography>

                <Typography>
                  {ticket.vehicleNumber}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  Priority
                </Typography>

                <Typography>
                  {ticket.priority}
                </Typography>
              </Grid>
            </Grid>
          )}

          <Controller
            name="technicianId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Select Technician"
              >
                {technicians.map(
                  (
                    technician
                  ) => (
                    <MenuItem
                      key={
                        technician.id
                      }
                      value={
                        technician.id
                      }
                    >
                      {
                        technician.name
                      }
                    </MenuItem>
                  )
                )}
              </TextField>
            )}
          />
                    <Controller
            name="remarks"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={3}
                label="Remarks"
                placeholder="Enter assignment remarks..."
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          startIcon={<EngineeringIcon />}
          onClick={handleSubmit(onSubmit)}
          disabled={
            loading || !ticket
          }
        >
          {loading
            ? "Assigning..."
            : "Assign Technician"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTechnicianDialog;
